<template>
  <div class="wrapper">
    <div class="animated fadeIn">
      <div class="card">
        <div class="card-header">
          <i class="icon-reload"></i> Auditlog
        </div>
        <div class="card-body">
          <div class="audit-filter-bar">
            <div class="audit-filter-toggles">
              <label class="audit-filter-toggle">
                <input v-model="displayImportant" type="checkbox">
                <span>Important entries</span>
              </label>
              <label class="audit-filter-toggle">
                <input v-model="displayReason" type="checkbox">
                <span>Missing reasons</span>
              </label>
              <label class="audit-filter-toggle">
                <input v-model="includeTestOnly" type="checkbox">
                <span>Include Test-only</span>
              </label>
              <div
                v-if="canUseAdvancedMode"
                class="audit-mode-toggle"
                role="group"
                aria-label="Audit log mode">
                <button
                  type="button"
                  :class="{ active: !visualGroupsEnabled }"
                  :aria-pressed="String(!visualGroupsEnabled)"
                  @click="toggleAuditLogMode(false)">
                  Standard
                </button>
                <button
                  type="button"
                  :class="{ active: visualGroupsEnabled }"
                  :aria-pressed="String(visualGroupsEnabled)"
                  @click="toggleAuditLogMode(true)">
                  Advanced
                </button>
              </div>
            </div>

            <div class="audit-filter-field audit-filter-staff">
              <label>Filter by Staff</label>
              <Autocomplete v-model="filterbyStaff" />
            </div>

            <div class="audit-filter-field">
              <label>Search</label>
              <input
                v-model="filterQuery"
                type="text"
                class="form-control"
                placeholder="Reason, entry, group or field...">
            </div>

            <div class="audit-filter-field">
              <label>Filter by Action</label>
              <select id="basicSelect" class="form-control" v-model="filterbyAction">
                <option value="">All</option>
                <template v-for="group in actionGroups">
                  <option :key="group.label" disabled class="audit-action-category">{{ group.label }}</option>
                  <option
                    v-for="action in group.options"
                    :key="group.label + '-' + action.value"
                    :value="action.value">
                    {{ action.label }}
                  </option>
                </template>
              </select>
            </div>
          </div>

          <b-overlay
            :show="logPending"
            variant="dark"
            opacity="0.55"
            blur="1px"
            rounded="sm">
            <template #overlay>
              <div class="text-center text-light">
                <b-spinner variant="info"></b-spinner>
                <div class="mt-2">Loading audit log...</div>
              </div>
            </template>
            <div :class="{ 'audit-log-loading-surface': logLoading && auditlog.length === 0 }">
              <AuditLogList
                :entries="auditlog"
                :groups="groups"
                :groups-enabled="moderationModeEnabled"
                :search-query="filterQuery"
                :editable-evidence="canDeleteAuditLog"
                :allow-delete-entries="canDeleteAuditLog"
                :display-only-important="displayImportant"
                @evidence-changed="fetchLog(false, false)">
              </AuditLogList>
            </div>
          </b-overlay>

          <br>
          <b-pagination size="md" :total-rows="count" v-model="page" v-on:change="fetchData" :per-page="100"></b-pagination>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapGetters, mapActions} from 'vuex'
import Autocomplete from './Permissions/UsernameSearch.vue'
import AuditLogList from '../../components/AuditLog/AuditLogList.vue'
import { buildAuditLogActionGroups } from '../../auditlog_permissions'

const groupsStorageKey = 'modtools_auditlog_groups_enabled'
const importantStorageKey = 'modtools_auditlog_important_enabled'

function storedBoolean (key, fallback) {
  const value = localStorage.getItem(key)
  if (value === null) {
    return fallback
  }
  return value === 'true'
}

export default {
  name: 'Auditlog',
  computed: {
    ...mapGetters({
      auditlog: 'Auditlog',
      groups: 'AuditlogGroups',
      groupRevision: 'AuditlogGroupRevision',
      logLoading: 'AuditlogLoading',
      logPending: 'AuditlogPending',
      count: 'LogCount'
    }),
    actionGroups () {
      return buildAuditLogActionGroups(permission => this.hasPermission(permission))
    },
    canUseAdvancedMode () {
      return this.hasPermission('AdvancedMode')
    },
    moderationModeEnabled () {
      return this.canUseAdvancedMode && this.groupsEnabled
    },
    canDeleteAuditLog () {
      return this.hasPermission('AuditLogDelete')
    }
  },
  data () {
    return {
      page: 1,
      filterbyStaff: null,
      filterQuery: '',
      filterTimer: null,
      modeFetchTimer: null,
      modeApplyFrame: null,
      modeApplySecondFrame: null,
      modeApplyTimer: null,
      revisionFetchTimer: null,
      requestSequence: Date.now(),
      displayImportant: storedBoolean(importantStorageKey, true),
      displayReason: false,
      includeTestOnly: false,
      groupsEnabled: storedBoolean(groupsStorageKey, false),
      visualGroupsEnabled: storedBoolean(groupsStorageKey, false),
      filterbyAction: ''
    }
  },
  created () {
    this.clearAuditlog()
  },
  components: {
    Autocomplete,
    AuditLogList
  },
  mounted () {
    this.fetchLog(true)
  },
  watch: {
    displayImportant () {
      localStorage.setItem(importantStorageKey, String(this.displayImportant))
      this.updateLog()
    },
    displayReason () {
      this.updateLog()
    },
    includeTestOnly () {
      this.updateLog()
    },
    groupsEnabled (enabled) {
      localStorage.setItem(groupsStorageKey, String(this.canUseAdvancedMode && enabled))
      this.page = 1
      this.scheduleModeFetch()
    },
    filterbyAction () {
      this.updateLog()
    },
    filterbyStaff () {
      this.updateLog()
    },
    filterQuery () {
      this.scheduleUpdate()
    },
    groupRevision () {
      this.scheduleRevisionFetch()
    }
  },
  beforeDestroy () {
    window.clearTimeout(this.filterTimer)
    window.clearTimeout(this.modeFetchTimer)
    window.clearTimeout(this.modeApplyTimer)
    window.clearTimeout(this.revisionFetchTimer)
    if (this.modeApplyFrame !== null) {
      window.cancelAnimationFrame(this.modeApplyFrame)
    }
    if (this.modeApplySecondFrame !== null) {
      window.cancelAnimationFrame(this.modeApplySecondFrame)
    }
  },
  methods: {
    ...mapActions(['clearAuditlog', 'startAuditlogLoading']),
    updateLog (showLoading = true) {
      this.page = 1
      this.fetchLog(true, showLoading)
    },
    toggleAuditLogMode (advanced) {
      const requestedMode = Boolean(advanced)
      const nextMode = this.visualGroupsEnabled === requestedMode ? !requestedMode : requestedMode
      this.visualGroupsEnabled = nextMode
      this.scheduleModeApply(nextMode)
    },
    scheduleModeApply (enabled) {
      if (this.modeApplyFrame !== null) {
        window.cancelAnimationFrame(this.modeApplyFrame)
        this.modeApplyFrame = null
      }
      if (this.modeApplySecondFrame !== null) {
        window.cancelAnimationFrame(this.modeApplySecondFrame)
        this.modeApplySecondFrame = null
      }
      window.clearTimeout(this.modeApplyTimer)

      const applyMode = () => {
        if (this.groupsEnabled !== enabled) {
          this.groupsEnabled = enabled
        }
      }

      if (window.requestAnimationFrame) {
        this.modeApplyFrame = window.requestAnimationFrame(() => {
          this.modeApplyFrame = null
          this.modeApplySecondFrame = window.requestAnimationFrame(() => {
            this.modeApplySecondFrame = null
            applyMode()
          })
        })
        return
      }

      this.modeApplyTimer = window.setTimeout(applyMode, 0)
    },
    scheduleUpdate () {
      window.clearTimeout(this.filterTimer)
      this.filterTimer = window.setTimeout(() => this.updateLog(), 250)
    },
    scheduleModeFetch () {
      window.clearTimeout(this.modeFetchTimer)
      this.modeFetchTimer = window.setTimeout(() => this.fetchLog(true, false), 120)
    },
    scheduleRevisionFetch () {
      window.clearTimeout(this.revisionFetchTimer)
      this.revisionFetchTimer = window.setTimeout(() => this.fetchLog(false, false), 120)
    },
    fetchData (e) {
      this.page = e
      this.scrollToPageTop()
      this.fetchLog(false)
    },
    scrollToPageTop () {
      this.$nextTick(() => {
        let element = this.$el
        while (element) {
          if (element.scrollHeight > element.clientHeight) {
            element.scrollTop = 0
          }
          element = element.parentElement
        }
        if (window.scrollTo) {
          window.scrollTo(0, 0)
        }
      })
    },
    fetchLog (count, showLoading = true) {
      const requestID = ++this.requestSequence
      this.startAuditlogLoading({ requestID, loading: showLoading })
      const staff = this.filterbyStaff && this.filterbyStaff.name ? this.filterbyStaff.name : ''
      const request = {
        request_id: requestID,
        page: this.page,
        count,
        display_only_important: this.displayImportant,
        display_only_reasons: this.displayReason,
        include_test_only: this.includeTestOnly,
        filterbyAction: this.filterbyAction,
        filter_by_user: staff,
        filter_by_staff: staff,
        filter_by_search: this.filterQuery.trim(),
        moderation_mode: this.moderationModeEnabled
      }
      this.emit('getAuditlog', request)
    }
  }
}
</script>

<style lang="scss" scoped>
.audit-log-loading-surface {
  min-height: 180px;
}

.audit-filter-bar {
  display: grid;
  grid-template-columns: minmax(220px, 1.2fr) minmax(180px, 1fr) minmax(240px, 1.4fr) minmax(220px, 1.2fr);
  gap: 14px;
  align-items: end;
  padding: 14px;
  margin-bottom: 18px;
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.audit-filter-toggles {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.audit-filter-toggle {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 34px;
  margin: 0;
  padding: 7px 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.16);
  font-size: 0.82rem;
}

.audit-filter-toggle input {
  margin: 0;
}

.audit-mode-toggle {
  display: inline-grid;
  grid-template-columns: repeat(2, minmax(82px, 1fr));
  min-height: 34px;
  padding: 3px;
  border: 1px solid rgba(99, 194, 222, 0.3);
  border-radius: 18px;
  background: rgba(0, 0, 0, 0.28);
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.45);
}

.audit-mode-toggle button {
  min-height: 28px;
  padding: 4px 12px;
  border: 0;
  border-radius: 14px;
  background: transparent;
  color: rgba(255, 255, 255, 0.62);
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.15s ease, box-shadow 0.15s ease, color 0.15s ease, transform 0.15s ease;
}

.audit-mode-toggle button:hover {
  color: #fff;
}

.audit-mode-toggle button:focus {
  outline: 0;
  box-shadow: none;
}

.audit-mode-toggle button.active {
  background: linear-gradient(135deg, #20a8d8, #1985ad);
  color: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35);
  transform: translateY(-1px);
}

.audit-mode-toggle button.active:focus {
  box-shadow: 0 0 0 2px rgba(99, 194, 222, 0.35), 0 2px 6px rgba(0, 0, 0, 0.35);
}

.audit-filter-field label {
  display: block;
  margin-bottom: 5px;
  color: rgba(255, 255, 255, 0.68);
  font-size: 0.74rem;
  font-weight: 700;
  text-transform: uppercase;
}

.audit-action-category {
  color: #8f9ba6;
  background: #1b2229;
}

@media (max-width: 1199px) {
  .audit-filter-bar {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .audit-filter-bar {
    grid-template-columns: 1fr;
  }

}
</style>
